import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  Divider,
  Grid,
  IconButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Slide,
  Snackbar,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import LucideIcon from "../../../components/LucideIcon";

import { MjmlToJson } from "easy-email-extensions";

import LoadingButton from "../../../components/LoadingButton";
import EmailBuilder, {
  EmailBuilderTemplate,
  renderTemplateToHtml,
} from "../../../components/EmailBuilder";
import { STARTER_TEMPLATES } from "../../../components/EmailBuilder/starterTemplates";
import { MJML_STARTER_TEMPLATES } from "../../../components/EmailBuilder/templates/manifest";
import { parseError, toCamelCase } from "../../../utils";

export interface SmartListHeaderOption {
  id?: string;
  name: string;
  type?: string;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface VisualEmailBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (bodyHtml: string, bodyDocument: EmailBuilderTemplate) => void | Promise<void>;
  onError: (err: string) => void;
  companyId: string;
  getToken: () => Promise<string>;
  /** Shown after "Visual Builder —" in the AppBar; usually the campaign or template name. */
  name: string;
  initialBodyDocument: EmailBuilderTemplate | null;
  /** Smart-list headers used to surface available {{variables}} in the top bar. */
  listHeaders?: SmartListHeaderOption[];
}

// Always-available system tokens injected by the marketing send pipeline.
// Canonical token is {{unsubscribeUrl}}; {{unsubscribe}} also resolves to the
// same value as a backwards-compatible alias.
const SYSTEM_VARIABLES: { key: string; label: string }[] = [
  { key: "unsubscribeUrl", label: "Unsubscribe URL" },
];

const VisualEmailBuilderDialog: React.FC<VisualEmailBuilderDialogProps> = ({
  open,
  onClose,
  onSave,
  onError,
  companyId,
  getToken,
  name,
  initialBodyDocument,
  listHeaders = [],
}) => {
  const [saving, setSaving] = useState(false);
  const documentRef = useRef<EmailBuilderTemplate | null>(initialBodyDocument);
  const [activeTemplate, setActiveTemplate] = useState<EmailBuilderTemplate | null>(initialBodyDocument);
  const [mountKey, setMountKey] = useState(0);
  const [variableMenuAnchor, setVariableMenuAnchor] = useState<null | HTMLElement>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const listVariables = useMemo(() => {
    const seen = new Set<string>();
    const items: { key: string; label: string }[] = [];
    for (const header of listHeaders) {
      if (!header?.name) continue;
      if (header.type === "action") continue;
      const key = toCamelCase(header.name);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      items.push({ key, label: header.name });
    }
    return items;
  }, [listHeaders]);

  const handleCopyVariable = async (key: string) => {
    const token = `{{${key}}}`;
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      // Clipboard API blocked (insecure context, permissions) — still close the
      // menu and surface a toast so the user knows the token; they can type it.
    }
    setCopiedToken(token);
    setVariableMenuAnchor(null);
  };

  useEffect(() => {
    if (!open) return;
    documentRef.current = initialBodyDocument;
    setActiveTemplate(initialBodyDocument);
    setMountKey((k) => k + 1);
  }, [open, initialBodyDocument]);

  const showStarterPicker = !activeTemplate || !activeTemplate.content;

  const handlePickStarter = (build: () => EmailBuilderTemplate) => {
    const tpl = build();
    documentRef.current = tpl;
    setActiveTemplate(tpl);
    setMountKey((k) => k + 1);
  };

  const handlePickMjmlTemplate = (mjml: string, title: string) => {
    try {
      const content = MjmlToJson(mjml);
      const tpl: EmailBuilderTemplate = {
        subject: title || name || "",
        subTitle: "",
        content,
      };
      documentRef.current = tpl;
      setActiveTemplate(tpl);
      setMountKey((k) => k + 1);
    } catch (err) {
      onError(parseError(err));
    }
  };

  const handleStartBlank = () => {
    documentRef.current = null;
    setActiveTemplate({ subject: name || "", subTitle: "", content: null as any });
    setMountKey((k) => k + 1);
  };

  const handleSave = async () => {
    const document = documentRef.current;
    if (!document || !document.content) {
      onError("Nothing to save yet.");
      return;
    }
    setSaving(true);
    try {
      const bodyHtml = renderTemplateToHtml(document);
      await onSave(bodyHtml, document);
      onClose();
    } catch (err) {
      onError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} aria-label="close">
            <LucideIcon name="X" />
          </IconButton>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flex: 1, ml: 2 }}>
            <Typography variant="h6">Visual Builder</Typography>
            {name && (
              <Typography variant="body2" color="text.secondary">
                — {name}
              </Typography>
            )}
          </Stack>
          <Tooltip
            arrow
            title="Click a variable to copy {{token}}; paste it into a text or button block."
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<LucideIcon name="Braces" size={18} />}
              onClick={(e) => setVariableMenuAnchor(e.currentTarget)}
              sx={{ mr: 1 }}
            >
              Variables
            </Button>
          </Tooltip>
          <Menu
            anchorEl={variableMenuAnchor}
            open={Boolean(variableMenuAnchor)}
            onClose={() => setVariableMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { maxHeight: 480, minWidth: 280 } } }}
          >
            <ListSubheader sx={{ lineHeight: "32px" }}>System</ListSubheader>
            {SYSTEM_VARIABLES.map((v) => (
              <MenuItem key={v.key} onClick={() => handleCopyVariable(v.key)}>
                <ListItemText
                  primary={v.label}
                  secondary={`{{${v.key}}}`}
                  secondaryTypographyProps={{
                    sx: { fontFamily: "monospace", fontSize: 12 },
                  }}
                />
                <LucideIcon name="Copy" size={18} style={{ marginLeft: 8, opacity: 0.6 }} />
              </MenuItem>
            ))}
            <Divider />
            <ListSubheader sx={{ lineHeight: "32px" }}>
              {listVariables.length > 0 ? "Smart list fields" : "Smart list fields (no list selected)"}
            </ListSubheader>
            {listVariables.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  Pick a smart list to see its fields here.
                </Typography>
              </MenuItem>
            )}
            {listVariables.map((v) => (
              <MenuItem key={v.key} onClick={() => handleCopyVariable(v.key)}>
                <ListItemText
                  primary={v.label}
                  secondary={`{{${v.key}}}`}
                  secondaryTypographyProps={{
                    sx: { fontFamily: "monospace", fontSize: 12 },
                  }}
                />
                <LucideIcon name="Copy" size={18} style={{ marginLeft: 8, opacity: 0.6 }} />
              </MenuItem>
            ))}
          </Menu>
          <Snackbar
            open={Boolean(copiedToken)}
            autoHideDuration={2000}
            onClose={() => setCopiedToken(null)}
            message={copiedToken ? `Copied ${copiedToken}` : ""}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          />
          <Tooltip
            arrow
            placement="bottom-end"
            title={
              <Box sx={{ p: 0.5, maxWidth: 320 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Tips
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Drag the <strong>Unsubscribe</strong> block from the Content
                  panel into your footer — it inserts a link to{" "}
                  <code>{"{{unsubscribe}}"}</code>, which is replaced with a
                  per-recipient unsubscribe URL when the email sends.
                </Typography>
                <Typography variant="body2">
                  You can also use <code>{"{{unsubscribe}}"}</code> as the link
                  target on any text or button you design. If your template
                  doesn't include one, we'll append a default unsubscribe
                  footer automatically.
                </Typography>
              </Box>
            }
          >
            <IconButton sx={{ mr: 1 }} aria-label="Email builder tips">
              <LucideIcon name="CircleHelp" />
            </IconButton>
          </Tooltip>
          <LoadingButton
            loading={saving}
            onClick={handleSave}
            variant="contained"
          >
            Save
          </LoadingButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {showStarterPicker ? (
          <Box sx={{ p: 4, maxWidth: 1200, mx: "auto" }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Start from a template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Pick a starter to drop in pre-built blocks, or start from scratch.
              You can edit everything once it's loaded.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
              Quick starters
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {STARTER_TEMPLATES.map((tpl) => (
                <Grid key={tpl.id} item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardActionArea
                      sx={{ height: "100%" }}
                      onClick={() => handlePickStarter(tpl.build)}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {tpl.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tpl.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ height: "100%", borderStyle: "dashed" }}>
                  <CardActionArea sx={{ height: "100%" }} onClick={handleStartBlank}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Blank
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start with an empty canvas
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Designed templates
            </Typography>
            <Grid container spacing={2}>
              {MJML_STARTER_TEMPLATES.map((tpl) => (
                <Grid key={tpl.id} item xs={12} sm={6} md={4} lg={3}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardActionArea
                      onClick={() => handlePickMjmlTemplate(tpl.mjml, tpl.title)}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 240,
                          backgroundColor: "action.hover",
                          backgroundImage: `url(${tpl.thumbnail})`,
                          backgroundSize: "cover",
                          backgroundPosition: "top center",
                          backgroundRepeat: "no-repeat",
                        }}
                      />
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {tpl.title}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <EmailBuilder
            key={mountKey}
            initialTemplate={activeTemplate}
            subject={name}
            onChange={(doc) => {
              documentRef.current = doc;
            }}
            getToken={getToken}
            companyId={companyId}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default VisualEmailBuilderDialog;
